import React, { Dispatch, SetStateAction, useState, useMemo, useCallback, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { getCantonOptions } from '../../data/cantons';
import { legendValid } from '../../utils/legend.data.util';
import { prefixLegend } from '../../scripts/name.legend.script';
import { useDropdownKeyboardNav } from '../../hooks/useDropdownKeyboardNav';

import AppLegend from './Legend';

/** Removes diacritics for accent-insensitive matching. */
function normalize(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export interface AppInputCantonProps {
  component?: string;
  options: { disabled: boolean };
  /**
   * The canton value.
   *
   * This component stores the **FR canonical name** (e.g. "Genève") to match
   * the current backend contract where administrative_area is a label, not a
   * code. When the migration to codes happens, only the onSelect callback
   * needs to change — the display logic already works with codes internally.
   */
  canton: { value: string; valid: boolean };
  setCanton: Dispatch<SetStateAction<{ value: string; valid: boolean }>>;
}

const AppInputCanton: React.FunctionComponent<AppInputCantonProps> = ({
  component, options, canton, setCanton
}) => {
  const { lang } = StateLanguage();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const langCode = lang.code?.toLowerCase() || 'fr';
  const cantonOptions = useMemo(() => getCantonOptions(langCode), [langCode]);

  const filtered = useMemo(() => {
    if (!search) return cantonOptions;
    const needle = normalize(search);
    return cantonOptions.filter(
      o => normalize(o.label).includes(needle) || normalize(o.value).includes(needle)
    );
  }, [search, cantonOptions]);

  const handleSelect = useCallback((code: string) => {
    setCanton({ value: code, valid: true });
    legendValid(`${prefixLegend(component)}legend-administrative-area-required`);
  }, [setCanton, component]);

  const handleToggle = (nextShow: boolean) => {
    setIsOpen(nextShow);
    if (nextShow) setSearch('');
  };

  const handleClose = useCallback(() => {
    toggleRef.current?.click();
  }, []);

  const { highlightedIndex, handleKeyDown, getOptionId, activeDescendant, listboxId } =
    useDropdownKeyboardNav({
      itemCount: filtered.length,
      onSelect: (idx) => { handleSelect(filtered[idx].value); handleClose(); },
      isOpen,
      onClose: handleClose,
      idPrefix: 'canton',
    });

  // Find the display label for the current value (which is a canton code internally)
  const selectedLabel = useMemo(() => {
    if (!canton.value) return null;
    const found = cantonOptions.find(o => o.value === canton.value);
    return found?.label ?? null;
  }, [canton.value, cantonOptions]);

  return (
    <>
      <Dropdown onToggle={handleToggle}>
        <Dropdown.Toggle
          ref={toggleRef}
          variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0"
          disabled={options.disabled}
        >
          <span className="selection">
            <span className="select2-selection select2-selection--single form-select">
              <span className="select2-selection__rendered" role="textbox">
                {selectedLabel ?? lang.labels.selectOption}
              </span>
            </span>
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-search select2-search--dropdown">
              <input
                className="select2-search__field"
                type="text"
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-activedescendant={activeDescendant}
                aria-expanded={isOpen}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </span>
            <span className="select2-results">
              <ul className="select2-results__options" id={listboxId} role="listbox">
                {filtered.length > 0
                  ? filtered.map((opt, idx) => (
                      <li
                        key={opt.value}
                        id={getOptionId(idx)}
                        className={`select2-results__option select2-results__option--selectable ${opt.value === canton.value ? 'select2-results__option--selected' : ''} ${idx === highlightedIndex ? 'select2-results__option--highlighted' : ''}`}
                        role="option"
                        aria-selected={opt.value === canton.value}
                      >
                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => { handleSelect(opt.value); handleClose(); }}>
                          {opt.label}
                        </Dropdown.Item>
                      </li>
                    ))
                  : <li className="select2-results__option select2-results__message" role="alert">
                      {lang.labels.noResultsFound}
                    </li>
                }
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      <AppLegend
        component={component}
        attribute={{ check: canton.valid, name: "administrative-area", index: null, sub_index: null }}
        container={{ valid: false, required: true, identical: false, exists: false }}
      />
    </>
  );
};

export default AppInputCanton;
